import React, { useState } from 'react';
import { X, ShieldAlert, CheckCircle, RefreshCw, AlertCircle, FileText } from 'lucide-react';
import Swal from 'sweetalert2';
import { reconcileDeliveries } from '../../../services/newsNewsletterApi';

export default function ReconciliationModal({
  isOpen,
  onClose,
  campaign,
  uncertainDeliveries = [],
  onReconciled,
}) {
  const [resolutions, setResolutions] = useState({});
  const [notes, setNotes] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !campaign) return null;

  const handleActionChange = (deliveryId, action) => {
    setResolutions((prev) => ({ ...prev, [deliveryId]: action }));
  };

  const handleNotesChange = (deliveryId, note) => {
    setNotes((prev) => ({ ...prev, [deliveryId]: note }));
  };

  const handleSubmitAll = async (e) => {
    e.preventDefault();

    // Check that every uncertain delivery has an action selected and non-empty notes
    const batch = uncertainDeliveries.map((del) => {
      const action = resolutions[del._id] || 'MARK_ACCEPTED';
      const note = notes[del._id] || 'Réconciliation administrative suite à interruption socket vérifiée dans les logs SMTP.';
      return {
        deliveryId: del._id,
        action,
        notes: note,
      };
    });

    setIsSubmitting(true);
    try {
      await reconcileDeliveries(campaign._id, batch);
      Swal.fire({
        title: 'Réconciliation effectuée !',
        text: 'Les livraisons incertaines ont été réconciliées. La campagne est désormais à jour.',
        icon: 'success',
      });
      onReconciled?.();
      onClose();
    } catch (err) {
      Swal.fire('Erreur', err.message || 'Échec de la réconciliation', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="an-modal-backdrop animate-fade-in" onClick={onClose}>
      <div className="an-modal-card an-modal-large" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="an-modal-header border-warning">
          <div className="an-modal-title text-warning">
            <ShieldAlert size={22} className="mr-2" />
            <span>Réconciliation Administrative SMTP</span>
          </div>
          <button className="an-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmitAll}>
          <div className="an-modal-body an-modal-scrollable">
            <div className="an-alert-box warning">
              <AlertCircle size={20} className="mr-2 flex-shrink-0" />
              <div>
                <strong>Incertitude de transmission SMTP :</strong> La connexion réseau ou le socket SMTP s’est interrompu après la commande DATA. Afin d'éviter les doubles envois aux clients, une décision humaine formelle est obligatoire avant toute relance.
              </div>
            </div>

            <div className="an-reconcile-list mt-3">
              {uncertainDeliveries.length === 0 ? (
                <div className="an-empty-state">Aucune livraison incertaine à réconcilier.</div>
              ) : (
                uncertainDeliveries.map((del) => {
                  const currentAction = resolutions[del._id] || 'MARK_ACCEPTED';
                  const currentNotes = notes[del._id] || '';

                  return (
                    <div key={del._id} className="an-reconcile-item-card">
                      <div className="an-reconcile-item-header">
                        <span className="an-reconcile-email">{del.recipientEmailSnapshot}</span>
                        <span className="an-badge an-badge-warning">
                          UNCERTAIN_NEEDS_RECONCILIATION
                        </span>
                      </div>

                      {del.errorMessage && (
                        <div className="an-reconcile-error">
                          <small className="text-muted">Détail technique :</small> {del.errorMessage}
                        </div>
                      )}

                      <div className="an-reconcile-actions-group mt-3">
                        <label className="font-semibold d-block mb-1">Décision administrative :</label>
                        <div className="an-radio-options">
                          <label className="an-radio-label">
                            <input
                              type="radio"
                              name={`action_${del._id}`}
                              value="MARK_ACCEPTED"
                              checked={currentAction === 'MARK_ACCEPTED'}
                              onChange={() => handleActionChange(del._id, 'MARK_ACCEPTED')}
                            />
                            <span>
                              <strong>Marquer comme Accepté (Délivré) :</strong> Le message est confirmé reçu dans les logs du relais SMTP. Aucun renvoi.
                            </span>
                          </label>

                          <label className="an-radio-label">
                            <input
                              type="radio"
                              name={`action_${del._id}`}
                              value="MARK_FAILED_ALLOW_RETRY"
                              checked={currentAction === 'MARK_FAILED_ALLOW_RETRY'}
                              onChange={() => handleActionChange(del._id, 'MARK_FAILED_ALLOW_RETRY')}
                            />
                            <span>
                              <strong>Marquer comme Échec Avéré :</strong> L'email n'a pas été envoyé. Le marquer prêt pour relance contrôlée.
                            </span>
                          </label>

                          <label className="an-radio-label">
                            <input
                              type="radio"
                              name={`action_${del._id}`}
                              value="SKIP"
                              checked={currentAction === 'SKIP'}
                              onChange={() => handleActionChange(del._id, 'SKIP')}
                            />
                            <span>
                              <strong>Ignorer définitivement (Skip) :</strong> Ne pas renvoyer cet email à ce destinataire.
                            </span>
                          </label>
                        </div>
                      </div>

                      <div className="an-form-group mt-2">
                        <label>Justification administrative (consignée dans l'audit) :</label>
                        <input
                          type="text"
                          className="an-input"
                          placeholder="Ex: Vérifié dans les journaux du relais SMTP Hostinger : 250 OK reçu..."
                          value={currentNotes}
                          onChange={(e) => handleNotesChange(del._id, e.target.value)}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="an-modal-footer">
            <button
              type="button"
              className="an-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="an-btn-primary"
              disabled={isSubmitting || uncertainDeliveries.length === 0}
            >
              <CheckCircle size={16} />
              <span>{isSubmitting ? 'Enregistrement...' : 'Valider la réconciliation'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

